import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Button,
  Box,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DeleteIcon from "@mui/icons-material/Delete";
import { SERVER_BASE_URL } from "../Config";

interface SalesRep {
  id: number;
  name: string;
}

const SalesRepManagement: React.FC = () => {
  const [reps, setReps] = useState<SalesRep[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newName, setNewName] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const token = localStorage.getItem("pharmacistToken");

  const loadReps = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${SERVER_BASE_URL}/admin/sales-reps`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setReps(data.salesReps);
      } else {
        setError(data.message || "Failed to load sales reps");
      }
    } catch {
      setError("Failed to load sales reps");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReps();
  }, []);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    try {
      const res = await fetch(`${SERVER_BASE_URL}/admin/sales-reps`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: newName.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccess("Sales rep added");
        setNewName("");
        setShowAddDialog(false);
        loadReps();
      } else {
        setError(data.message || "Failed to add sales rep");
      }
    } catch {
      setError("Failed to add sales rep");
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await fetch(`${SERVER_BASE_URL}/admin/sales-reps/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setSuccess("Sales rep deleted");
        setDeleteConfirmId(null);
        loadReps();
      } else {
        setError(data.message || "Failed to delete sales rep");
      }
    } catch {
      setError("Failed to delete sales rep");
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography sx={{ typography: { xs: "h6", sm: "h5" } }}>Manage Sales Reps</Typography>
        <Button
          variant="contained"
          startIcon={isMobile ? undefined : <PersonAddIcon />}
          size={isMobile ? "small" : "medium"}
          onClick={() => setShowAddDialog(true)}
        >
          {isMobile ? "Add" : "Add Sales Rep"}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {isMobile ? (
        reps.map((rep) => (
          <Card key={rep.id} sx={{ mb: 1 }}>
            <CardContent sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, "&:last-child": { pb: 1 } }}>
              <Typography>{rep.name}</Typography>
              <IconButton color="error" onClick={() => setDeleteConfirmId(rep.id)}>
                <DeleteIcon />
              </IconButton>
            </CardContent>
          </Card>
        ))
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reps.map((rep) => (
                <TableRow key={rep.id}>
                  <TableCell>{rep.name}</TableCell>
                  <TableCell align="right">
                    <IconButton color="error" onClick={() => setDeleteConfirmId(rep.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {reps.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    No sales reps configured
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Sales Rep</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Name"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            sx={{ mt: 1 }}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={!newName.trim()}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle>Delete Sales Rep?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this sales rep?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesRepManagement;
